<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Containers</h1>
				<p class="text-muted mb-0">
					Your district's container definitions, and the containers it holds.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- Definitions: the measured sizes the district counts by. A definition
		     is product-specific, so it names the catalog product it measures. -->
		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-0">Container Definitions</h2>
				<p class="text-muted mb-3 small">
					A definition is one measured size of one product — a 40 lb bag, a 2.5
					gallon jug.
				</p>

				<form
					class="row g-2 align-items-end mb-3"
					@submit.prevent="createDefinition"
				>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="definitionProduct">Product</label>
						<select
							id="definitionProduct"
							v-model="newDefinition.product_catalog_id"
							class="form-select"
							required
						>
							<option value="" disabled>Choose a product</option>
							<option
								v-for="product in catalog"
								:key="product.product_id"
								:value="product.product_id"
							>
								{{ product.name }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-3 col-lg-2">
						<label class="form-label" for="definitionName">Name</label>
						<input
							id="definitionName"
							v-model="newDefinition.name"
							type="text"
							class="form-control"
							placeholder="e.g. 40 lb bag"
							required
						/>
					</div>
					<div class="col-6 col-md-2">
						<label class="form-label" for="definitionAmount"
							>Measure amount</label
						>
						<input
							id="definitionAmount"
							v-model.number="newDefinition.measure_amount"
							type="number"
							min="0"
							step="any"
							class="form-control"
							required
						/>
					</div>
					<div class="col-6 col-md-2">
						<label class="form-label" for="definitionUnit">Measure unit</label>
						<input
							id="definitionUnit"
							v-model="newDefinition.measure_unit"
							type="text"
							class="form-control"
							placeholder="lb, gal…"
							required
						/>
					</div>
					<div class="col-12 col-md-auto">
						<ButtonLoading
							type="submit"
							text="Add definition"
							icon="bi bi-plus-lg"
							variant="primary"
							:loading="savingDefinition"
							:disabled="!definitionValid"
						/>
					</div>
				</form>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="definitions.length === 0"
					class="text-center py-4 text-muted"
				>
					No container definitions yet. Add one above.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Product</th>
								<th scope="col">Name</th>
								<th scope="col">Measure</th>
								<th scope="col">Status</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="definition in definitions"
								:key="definition.id"
								:class="{ 'text-muted': definition.retired }"
							>
								<td>{{ definition.product_name }}</td>
								<td class="fw-semibold">{{ definition.name }}</td>
								<td>
									{{ formatAmount(definition.measure_amount) }}
									{{ definition.measure_unit }}
								</td>
								<td>
									<span
										class="badge rounded-pill"
										:class="definition.retired ? 'bg-secondary' : 'bg-success'"
									>
										{{ definition.retired ? "Retired" : "Active" }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Held containers: product measured into a definition and sitting at a
		     location. A container is inventory in its own right. -->
		<div class="card mt-4">
			<div class="card-body">
				<h2 class="h5 mb-0">Held Containers</h2>
				<p class="text-muted mb-3 small">
					Product measured into a definition, held at a storage location.
				</p>

				<form
					class="row g-2 align-items-end mb-3"
					@submit.prevent="createContainer"
				>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="containerDefinition"
							>Definition</label
						>
						<select
							id="containerDefinition"
							v-model="newContainer.container_definition_id"
							class="form-select"
							required
						>
							<option :value="null" disabled>Choose a definition</option>
							<option
								v-for="definition in activeDefinitions"
								:key="definition.id"
								:value="definition.id"
							>
								{{ definition.product_name }} — {{ definition.name }} ({{
									formatAmount(definition.measure_amount)
								}}{{ definition.measure_unit }})
							</option>
						</select>
					</div>
					<div class="col-12 col-md-3 col-lg-3">
						<label class="form-label" for="containerLocation">Location</label>
						<select
							id="containerLocation"
							v-model="newContainer.storage_location_id"
							class="form-select"
							required
						>
							<option :value="null" disabled>Choose a location</option>
							<option
								v-for="location in activeLocations"
								:key="location.id"
								:value="location.id"
							>
								{{ location.path || location.name }}
							</option>
						</select>
					</div>
					<div class="col-6 col-md-2">
						<label class="form-label" for="containerAmount">Amount</label>
						<input
							id="containerAmount"
							v-model.number="newContainer.amount"
							type="number"
							min="0"
							step="any"
							class="form-control"
							required
						/>
					</div>
					<div class="col-12 col-md-auto">
						<ButtonLoading
							type="submit"
							text="Add container"
							icon="bi bi-plus-lg"
							variant="primary"
							:loading="savingContainer"
							:disabled="!containerValid"
						/>
					</div>
				</form>

				<div v-if="containers.length === 0" class="text-center py-4 text-muted">
					No containers held yet.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Definition</th>
								<th scope="col">Product</th>
								<th scope="col">Location</th>
								<th scope="col">Amount</th>
								<th scope="col">Drawn from</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="container in containers" :key="container.id">
								<td>{{ container.definition_name }}</td>
								<td>{{ container.product_name }}</td>
								<td>
									{{ container.location_name ?? "—" }}
								</td>
								<td>{{ formatAmount(container.amount) }}</td>
								<td>
									{{
										container.drawn_from_container_id !== null
											? `#${container.drawn_from_container_id}`
											: "—"
									}}
								</td>
								<td class="text-end">
									<ButtonLoading
										text="Subdivide"
										icon="bi bi-diagram-2"
										variant="outline-secondary"
										size="sm"
										@click="openSubdivide(container)"
									/>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Subdividing consumes the source container and yields smaller ones.
		     Some product is lost on the bench, so the waste is stated at the
		     step, against the container it came from. -->
		<div v-if="subdivideTarget" class="card mt-4 border-primary">
			<div class="card-body">
				<h2 class="h5 mb-3">
					Subdivide {{ subdivideTarget.definition_name }}
					<span class="text-muted fw-normal">
						— {{ formatAmount(subdivideTarget.amount) }} held
					</span>
				</h2>

				<div
					v-for="(target, index) in subdivideTargets"
					:key="index"
					class="row g-2 align-items-end mb-2"
				>
					<div class="col-12 col-md-6">
						<label class="form-label" :for="`subdivideDefinition${index}`">
							Target definition
						</label>
						<select
							:id="`subdivideDefinition${index}`"
							v-model="target.container_definition_id"
							class="form-select"
						>
							<option :value="null" disabled>Choose a definition</option>
							<option
								v-for="definition in activeDefinitions"
								:key="definition.id"
								:value="definition.id"
							>
								{{ definition.product_name }} — {{ definition.name }}
							</option>
						</select>
					</div>
					<div class="col-6 col-md-3">
						<label class="form-label" :for="`subdivideAmount${index}`">
							Amount
						</label>
						<input
							:id="`subdivideAmount${index}`"
							v-model.number="target.amount"
							type="number"
							min="0"
							step="any"
							class="form-control"
						/>
					</div>
					<div class="col-6 col-md-auto">
						<ButtonLoading
							text="Remove"
							icon="bi bi-trash"
							variant="outline-danger"
							size="sm"
							:disabled="subdivideTargets.length <= 1"
							@click="removeTarget(index)"
						/>
					</div>
				</div>

				<ButtonLoading
					text="Add target"
					icon="bi bi-plus-lg"
					variant="outline-secondary"
					size="sm"
					class="mb-3"
					@click="addTarget"
				/>

				<div class="row g-2 align-items-end mb-3">
					<div class="col-6 col-md-3">
						<label class="form-label" for="subdivideWaste">Waste</label>
						<input
							id="subdivideWaste"
							v-model.number="subdivideWaste"
							type="number"
							min="0"
							step="any"
							class="form-control"
						/>
					</div>
					<div class="col-12 col-md-6">
						<label class="form-label" for="subdivideReason">Reason</label>
						<input
							id="subdivideReason"
							v-model="subdivideReason"
							type="text"
							class="form-control"
							placeholder="e.g. Repackaged into field bottles"
						/>
					</div>
				</div>

				<div class="d-flex align-items-center gap-3">
					<ButtonLoading
						text="Subdivide"
						icon="bi bi-diagram-2"
						variant="primary"
						:loading="savingSubdivide"
						:disabled="!subdivideValid"
						@click="submitSubdivide"
					/>
					<ButtonLoading
						text="Cancel"
						variant="outline-secondary"
						@click="cancelSubdivide"
					/>
					<span class="text-muted small">
						This consumes {{ formatAmount(subdivideTotal) }} of the
						{{ formatAmount(subdivideTarget.amount) }} held.
					</span>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	ContainerCreate,
	ContainerDefinition,
	ContainerDefinitionCreate,
	ContainerSubdivide,
	InventoryContainer,
	InventoryLocation,
	PesticideCatalogProduct,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// The catalog is the source of the slugs a definition names; the locations are
// where a held container sits.
const catalog = ref<PesticideCatalogProduct[]>([]);
const definitions = ref<ContainerDefinition[]>([]);
const containers = ref<InventoryContainer[]>([]);
const locations = ref<InventoryLocation[]>([]);

const loading = ref(false);
const error = ref("");

const activeDefinitions = computed(() =>
	definitions.value.filter((definition) => !definition.retired),
);
const activeLocations = computed(() =>
	[...locations.value]
		.filter((location) => !location.retired)
		.sort((a, b) => a.path.localeCompare(b.path)),
);

// Amounts are floats on the wire; render them without trailing float noise.
function formatAmount(value: number): string {
	if (Number.isInteger(value)) return String(value);
	return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		// The three inventory collections through the generic QUERY endpoints (the
		// legacy GET lists' replacement, doc/in-progress/query.md §11 Phase 4),
		// each walked whole; the shared catalog beside them is another resource's
		// page and keeps its GET.
		const [catalogData, definitionPage, containerPage, locationPage] =
			await Promise.all([
				apiClient.JSONGet<{
					products: PesticideCatalogProduct[];
					total: number;
				}>("/api/pesticide-catalog"),
				apiClient.JSONQueryAll<ContainerDefinition>(
					"/api/inventory/container-definition",
				),
				apiClient.JSONQueryAll<InventoryContainer>("/api/inventory/container"),
				apiClient.JSONQueryAll<InventoryLocation>("/api/inventory/location"),
			]);
		catalog.value = catalogData.products ?? [];
		definitions.value = definitionPage.items;
		containers.value = containerPage.items;
		locations.value = locationPage.items;
	} catch (e: any) {
		error.value = e?.message || "Failed to load inventory containers.";
		showNotification("Failed to load containers", error.value);
	} finally {
		loading.value = false;
	}
}

// A definition's amount changes the source container's stock on subdivide, so
// the held list is re-read from the server rather than patched in place.
async function refreshContainers() {
	const page = await apiClient.JSONQueryAll<InventoryContainer>(
		"/api/inventory/container",
	);
	containers.value = page.items;
}

// ---- Container definitions ------------------------------------------------

const newDefinition = reactive({
	product_catalog_id: "",
	name: "",
	measure_amount: 0,
	measure_unit: "",
});
const savingDefinition = ref(false);

const definitionValid = computed(
	() =>
		newDefinition.product_catalog_id !== "" &&
		newDefinition.name.trim() !== "" &&
		newDefinition.measure_amount > 0 &&
		newDefinition.measure_unit.trim() !== "",
);

async function createDefinition() {
	if (!definitionValid.value) return;
	savingDefinition.value = true;
	const body: ContainerDefinitionCreate = {
		product_catalog_id: newDefinition.product_catalog_id,
		name: newDefinition.name.trim(),
		measure_amount: newDefinition.measure_amount,
		measure_unit: newDefinition.measure_unit.trim(),
	};
	try {
		const created = await apiClient.JSONPost<ContainerDefinition>(
			"/api/inventory/container-definition",
			body,
		);
		definitions.value = [...definitions.value, created];
		newDefinition.product_catalog_id = "";
		newDefinition.name = "";
		newDefinition.measure_amount = 0;
		newDefinition.measure_unit = "";
		showNotification(
			"Definition added",
			`"${created.name}" is ready to hold containers.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to add definition",
			e?.message || "Please try again.",
		);
	} finally {
		savingDefinition.value = false;
	}
}

// ---- Held containers ------------------------------------------------------

const newContainer = reactive<{
	container_definition_id: number | null;
	storage_location_id: number | null;
	amount: number;
}>({ container_definition_id: null, storage_location_id: null, amount: 0 });
const savingContainer = ref(false);

const containerValid = computed(
	() =>
		newContainer.container_definition_id !== null &&
		newContainer.storage_location_id !== null &&
		newContainer.amount > 0,
);

async function createContainer() {
	if (!containerValid.value) return;
	savingContainer.value = true;
	const body: ContainerCreate = {
		container_definition_id: newContainer.container_definition_id as number,
		storage_location_id: newContainer.storage_location_id as number,
		amount: newContainer.amount,
	};
	try {
		const created = await apiClient.JSONPost<InventoryContainer>(
			"/api/inventory/container",
			body,
		);
		await refreshContainers();
		newContainer.amount = 0;
		showNotification(
			"Container added",
			`A ${created.definition_name} is now held.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to add container",
			e?.message || "Please try again.",
		);
	} finally {
		savingContainer.value = false;
	}
}

// ---- Subdividing ----------------------------------------------------------

const subdivideTarget = ref<InventoryContainer | null>(null);
const subdivideTargets = ref<
	{ container_definition_id: number | null; amount: number }[]
>([]);
const subdivideWaste = ref(0);
const subdivideReason = ref("");
const savingSubdivide = ref(false);

function openSubdivide(container: InventoryContainer) {
	subdivideTarget.value = container;
	subdivideTargets.value = [{ container_definition_id: null, amount: 0 }];
	subdivideWaste.value = 0;
	subdivideReason.value = "";
}

function cancelSubdivide() {
	subdivideTarget.value = null;
}

function addTarget() {
	subdivideTargets.value.push({ container_definition_id: null, amount: 0 });
}

function removeTarget(index: number) {
	subdivideTargets.value.splice(index, 1);
}

const subdivideTotal = computed(
	() =>
		subdivideTargets.value.reduce(
			(sum, target) => sum + (target.amount || 0),
			0,
		) + (subdivideWaste.value || 0),
);

const subdivideValid = computed(
	() =>
		subdivideTarget.value !== null &&
		subdivideTargets.value.some(
			(target) => target.container_definition_id !== null && target.amount > 0,
		),
);

async function submitSubdivide() {
	const source = subdivideTarget.value;
	if (!source) return;
	const targets = subdivideTargets.value.filter(
		(target) => target.container_definition_id !== null && target.amount > 0,
	);
	// The server refuses an overdraw, but stating it here keeps the bench from
	// being told after the fact.
	if (subdivideTotal.value > source.amount) {
		showNotification(
			"Not enough product",
			`The targets and waste total ${formatAmount(subdivideTotal.value)}, more than the ${formatAmount(source.amount)} held.`,
		);
		return;
	}
	savingSubdivide.value = true;
	const reason = subdivideReason.value.trim();
	const body: ContainerSubdivide = {
		targets: targets.map((target) => ({
			container_definition_id: target.container_definition_id as number,
			amount: target.amount,
		})),
		waste: subdivideWaste.value || 0,
		...(reason !== "" ? { reason } : {}),
	};
	try {
		const created = await apiClient.JSONPost<InventoryContainer[]>(
			`/api/inventory/container/${source.id}/subdivide`,
			body,
		);
		await refreshContainers();
		subdivideTarget.value = null;
		showNotification(
			"Container subdivided",
			`Created ${created.length} container${created.length === 1 ? "" : "s"}.`,
		);
	} catch (e: any) {
		showNotification("Failed to subdivide", e?.message || "Please try again.");
	} finally {
		savingSubdivide.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
